// SiriDharini Blog API Service
import { API_BASE_URL, API_KEY } from '../config/api';

class SiridhariniBlogService {
  // Public endpoints for blog display
  
  /**
   * Get all published blogs for public display
   */
  static async getAllPublishedBlogs() {
    try {
      console.log('🌐 Fetching all published SiriDharini blogs...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully fetched', blogs.length, 'published blogs');
      return blogs;
    } catch (error) {
      console.error('❌ Error fetching published blogs:', error);
      // Return fallback data in case of API failure
      return [];
    }
  }

  /**
   * Get blog by slug for public display
   */
  static async getBlogBySlug(slug) {
    try {
      console.log('🔍 Fetching SiriDharini blog by slug:', slug);
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/slug/${slug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blog = await response.json();
      console.log('✅ Successfully fetched blog:', blog.title);
      return blog;
    } catch (error) {
      console.error('❌ Error fetching blog by slug:', error);
      throw error;
    }
  }

  /**
   * Get blogs by category
   */
  static async getBlogsByCategory(category) {
    try {
      console.log('📂 Fetching SiriDharini blogs by category:', category);
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/category/${category}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully fetched', blogs.length, 'blogs in category:', category);
      return blogs;
    } catch (error) {
      console.error('❌ Error fetching blogs by category:', error);
      return [];
    }
  }

  /**
   * Get featured blogs
   */
  static async getFeaturedBlogs() {
    try {
      console.log('⭐ Fetching featured SiriDharini blogs...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/featured`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully fetched', blogs.length, 'featured blogs');
      return blogs;
    } catch (error) {
      console.error('❌ Error fetching featured blogs:', error);
      return [];
    }
  }

  /**
   * Search published blogs
   */
  static async searchBlogs(searchTerm) {
    try {
      console.log('🔍 Searching SiriDharini blogs for:', searchTerm);
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully found', blogs.length, 'blogs matching search');
      return blogs;
    } catch (error) {
      console.error('❌ Error searching blogs:', error);
      return [];
    }
  }

  /**
   * Get recent blogs
   */
  static async getRecentBlogs(limit = 5) {
    try {
      console.log('🆕 Fetching recent SiriDharini blogs...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/recent?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully fetched', blogs.length, 'recent blogs');
      return blogs;
    } catch (error) {
      console.error('❌ Error fetching recent blogs:', error);
      return [];
    }
  }

  /**
   * Get similar blogs
   */
  static async getSimilarBlogs(blogId, limit = 3) {
    try {
      console.log('🔗 Fetching similar blogs for blog ID:', blogId);
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/${blogId}/similar?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blogs = await response.json();
      console.log('✅ Successfully fetched', blogs.length, 'similar blogs');
      return blogs;
    } catch (error) {
      console.error('❌ Error fetching similar blogs:', error);
      return [];
    }
  }

  /**
   * Get all categories
   */
  static async getAllCategories() {
    try {
      console.log('📂 Fetching all SiriDharini blog categories...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const categories = await response.json();
      console.log('✅ Successfully fetched', categories.length, 'categories');
      return categories;
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      return [];
    }
  }

  /**
   * Get all authors
   */
  static async getAllAuthors() {
    try {
      console.log('👤 Fetching all SiriDharini blog authors...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/authors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const authors = await response.json();
      console.log('✅ Successfully fetched', authors.length, 'authors');
      return authors;
    } catch (error) {
      console.error('❌ Error fetching authors:', error);
      return [];
    }
  }

  /**
   * Get all tags
   */
  static async getAllTags() {
    try {
      console.log('🏷️ Fetching all SiriDharini blog tags...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/public/tags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const tags = await response.json();
      console.log('✅ Successfully fetched', tags.length, 'tags');
      return tags;
    } catch (error) {
      console.error('❌ Error fetching tags:', error);
      return [];
    }
  }

  /**
   * Utility method to format date for display
   */
  static formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  /**
   * Utility method to truncate text
   */
  static truncateText(text, maxLength = 150) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  /**
   * Utility method to generate blog URL
   */
  static getBlogUrl(blog) {
    return `/blogs/${blog.slug || blog.id}`;
  }


  /**
   * Utility method to get category color
   */
  static getCategoryColor(category) {
    const categoryColors = {
      'INVESTMENT': '#E67E22',
      'FORESTRY': '#27AE60',
      'SERVICES': '#3498DB',
      'DOCUMENTS': '#8E44AD',
      'NEWS': '#E74C3C',
      'TIPS': '#F39C12'
    };

    return categoryColors[category] || '#34495E';
  }

  /**
   * Test connectivity to the API
   */
  static async testConnection() {
    try {
      console.log('🔌 Testing SiriDharini Blog API connection...');
      const response = await fetch(`${API_BASE_URL}/siridharini-blogs/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const health = await response.json();
      console.log('✅ SiriDharini Blog API is healthy:', health);
      return true;
    } catch (error) {
      console.error('❌ SiriDharini Blog API connection failed:', error);
      return false;
    }
  }
}

export default SiridhariniBlogService;